import { Grid, Stack } from '@hiiretail/react-foundation-ui';
import { toTransactionCreateRequest } from 'app/services/fmsrService/fmsr.types';
import { useRegisterTransactionMutation } from 'app/services/fmsrService/fmsrService';
import { useGetTaxRulesMutation } from 'app/services/taxRulesService/taxRulesService';
import {
  DEFAULT_RECEIPT_VALUES,
  DialogFormData,
  ReceiptFormData,
} from 'common/receipt.types';
import { formatDate } from 'common/utils/formatDate';
import { ItemSearch } from 'features/ItemSearch';
import { AddEditItemFormDialog } from 'features/addEditItem/AddEditItem';
import { selectWorkstation } from 'features/workstationSlice';
import useStartSession from 'hooks/useStartSession';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { Toaster } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useIsOffline } from 'hooks/useIsOffline';
import { CustomerInfoForm } from './components/CustomerInfoForm';
import { ItemsTable } from './components/ItemsTable';
import { NewReceiptTitle } from './components/NewReceiptTitle';
import { ReceiptInformation } from './components/ReceiptInformation';
import { SubmitReceiptButton } from './components/SubmitReceiptButton';
import { ReceiptTabs } from './components/Tabs';
import { VATSummaryGroup } from './components/VATSummary';

const styles = {
  main: {
    height: '100%',
    px: 3,
    gap: 3,
  },
  container: {
    display: 'flex',
  },
  form: {
    height: '100%',
    gap: 4,
  },
  grid1: {
    justifyContent: 'space-between',
    rowGap: 2,
  },
  grid2: {
    columnGap: 2,
    rowSpacing: 4,
    alignItems: 'center',
    justifyContent: 'end',
    pb: 3,
  },
};

export const NewReceipt: FC = () => {
  useStartSession();
  useIsOffline();

  const { businessUnitId, workstationId, operatorId } = useSelector(selectWorkstation);

  const [getRates, { isLoading, data: rates, error, reset: resetTaxRulesQuery }] =
    useGetTaxRulesMutation();
  const [
    registerTransaction,
    { isLoading: isTransactionLoading, isSuccess, reset: resetRegisterTransaction },
  ] = useRegisterTransactionMutation();

  const form = useForm<ReceiptFormData>({
    defaultValues: DEFAULT_RECEIPT_VALUES,
    mode: 'onChange',
  });

  const { control, handleSubmit, watch, reset } = form;

  const {
    fields: lineItems,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const {
    fields: VATSummaryRows,
    append: addVATLine,
    remove: removeVATLine,
  } = useFieldArray({
    control,
    name: 'VATSummary',
  });

  const taxableGroupIds = useMemo(
    () => Array.from(
      new Set(
        lineItems.flatMap((lineItem) => {
          const parentTaxGroupId = lineItem.item.taxableGroupId;
          const ecoTaxGroupIds = lineItem.item.ecoTaxItems.map((ecotax) => ecotax.taxableGroupId);
          return ecoTaxGroupIds.concat(parentTaxGroupId);
        }),
      ),
    ),
    [lineItems],
  );
  const tableRows = lineItems.map((field) => ({ ...field.item, uuid: field.uuid }));
  const invoiceDate = watch('invoiceDate');

  const handleAppendItem = (item: DialogFormData) => {
    append({
      uuid: crypto.randomUUID(),
      item,
    });

    const newTaxableGroupIds = [
      item.taxableGroupId,
      ...item.ecoTaxItems.map((ecoTaxItem) => ecoTaxItem.taxableGroupId),
    ];

    newTaxableGroupIds.forEach((taxableGroupId) => {
      if (!taxableGroupIds.includes(taxableGroupId)) {
        addVATLine({
          basePrice: '',
          VATAmount: '',
          taxableGroupId,
        });
      }
    });
  };

  const handleEditItem = (updatedItem: DialogFormData, index: number) => {
    update(index, { item: updatedItem, uuid: lineItems[index].uuid });
  };
  
  const onSubmit: SubmitHandler<ReceiptFormData> = (formData) =>
    registerTransaction(
      toTransactionCreateRequest(formData, { businessUnitId, operatorId, workstationId }),
    );

  useEffect(() => {
    if (isSuccess) {
      reset(DEFAULT_RECEIPT_VALUES);
      resetTaxRulesQuery();
      resetRegisterTransaction();
    }
  }, [isSuccess, reset, resetRegisterTransaction, resetTaxRulesQuery]);

  useEffect(() => {
    if (taxableGroupIds.length !== 0) {
      getRates({
        businessUnitId,
        taxableGroupIds,
        invoiceDate: formatDate(invoiceDate),
      });
    }
  }, [businessUnitId, getRates, invoiceDate, taxableGroupIds]);

  useEffect(() => {
    VATSummaryRows.forEach((vatRow, index) => {
      if (!taxableGroupIds.includes(vatRow.taxableGroupId)) {
        removeVATLine(index);
      }
    });
  }, [removeVATLine, taxableGroupIds, VATSummaryRows]);

  return (
    <Stack sx={styles.main}>
      <NewReceiptTitle />
      <FormProvider {...form}>
        <Stack component="form" onSubmit={handleSubmit(onSubmit)} sx={styles.form}>
          <Grid container sx={styles.grid1}>
            <ItemSearch />
            <ReceiptInformation />
          </Grid>
          <ReceiptTabs>
            <ItemsTable rows={tableRows} onRemoveRow={remove} />
            <VATSummaryGroup
              rows={VATSummaryRows}
              rates={rates ?? []}
              isLoading={isLoading}
              error={error}
            />
            <CustomerInfoForm />
          </ReceiptTabs>
          <Grid container sx={styles.grid2}>
            <SubmitReceiptButton loading={isTransactionLoading} />
          </Grid>
        </Stack>
        <AddEditItemFormDialog
          onAppendItem={handleAppendItem}
          onEditItem={handleEditItem}
          invoiceDate={invoiceDate}
        />
      </FormProvider>
      <Toaster
        position="bottom-left"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          duration: 5000,
        }}
      />
    </Stack>
  );
};
